// ===================================================================
// paymentService.ts - Service de gestion des paiements Stripe
// ===================================================================

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import subscriptionService, { SubscriptionTier } from './subscriptionService';

// Import de la configuration centralisée
import stripeConfig, { validateStripeConfig } from '../config/stripe';

interface CreateCheckoutSessionParams {
  priceId: string;
  customerEmail: string;
  userId?: string;
  plan: 'monthly' | 'yearly';
}

interface VerifyPaymentResult {
  success: boolean;
  tier?: SubscriptionTier;
  error?: string;
}

class PaymentService {
  private stripe: Stripe | null = null;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  /**
   * Initialiser le service Stripe
   */
  async initialize(): Promise<void> {
    // Si déjà initialisé, retourner immédiatement
    if (this.isInitialized && this.stripe) {
     
      return;
    }

    // Si une initialisation est en cours, attendre
    if (this.initializationPromise) {
     
      return this.initializationPromise;
    }

    // Créer une nouvelle promesse d'initialisation
    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  /**
   * Effectuer l'initialisation réelle
   */
  private async _performInitialization(): Promise<void> {
    try {

      // ✅ Valider la configuration
      const validation = validateStripeConfig();
      if (!validation.isValid) {
throw new Error('Configuration Stripe invalide');
      }

      // ✅ Charger Stripe
      this.stripe = await loadStripe(stripeConfig.publishableKey);

      if (!this.stripe) {
        throw new Error('Échec du chargement de Stripe');
      }

      this.isInitialized = true;

    } catch (error) {
    
      this.isInitialized = false;
      this.stripe = null;
      throw error;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Vérifier que le service est initialisé
   */
  private ensureInitialized(): void {
    if (!this.isInitialized || !this.stripe) {
      throw new Error('PaymentService non initialisé. Appelez initialize() d\'abord.');
    }
  }

  /**
   * Créer une session de paiement Stripe Checkout
   */
  async createCheckoutSession(params: CreateCheckoutSessionParams): Promise<{ sessionId: string; url: string }> {

    // Vérifier l'initialisation
    this.ensureInitialized();

    try {
      // ✅ Récupérer le token de session pour l'authentification
      const { supabase } = await import('./supabaseAudioService');
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error('Authentification requise pour créer une session de paiement');
      }

      // ✅ Appel au backend pour créer la session
      const response = await fetch(`${stripeConfig.apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: params.priceId,
          customerEmail: params.customerEmail,
          userId: params.userId,
          plan: params.plan,
          platform: Capacitor.isNativePlatform() ? 'capacitor' : 'web',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (!data.sessionId || !data.url) {
        throw new Error('Session ID ou URL manquant dans la réponse');
      }

      return { sessionId: data.sessionId, url: data.url };

    } catch (error) {
if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Erreur lors de la création de la session de paiement');
      }
    }
  }

  /**
   * Rediriger vers Stripe Checkout
   */
  async redirectToCheckout(sessionId: string, url: string): Promise<void> {

    // Sur l'app native, on ouvre le checkout dans le navigateur in-app (Custom Tab)
    // pour rester dans l'app et pouvoir intercepter le retour via deep link,
    // au lieu de faire une navigation plein écran qui bascule vers Chrome (même pattern que GoogleAuth)
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url, presentationStyle: 'fullscreen' });
      return;
    }

    // Vérifier l'initialisation
    this.ensureInitialized();

    try {
      const { error } = await this.stripe!.redirectToCheckout({ sessionId });

      if (error) {
throw new Error(error.message || 'Erreur lors de la redirection vers Stripe');
      }

    } catch (error) {
throw error;
    }
  }

  /**
   * Vérifier un paiement après retour de Stripe
   */
  async verifyPayment(sessionId: string): Promise<boolean> {

    try {
      const url = `${stripeConfig.apiUrl}/api/stripe/verify-payment`;

      // ✅ Récupérer le token de session pour l'authentification
      const { supabase } = await import('./supabaseAudioService');
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      // ✅ Appel au backend pour vérifier le paiement
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
      
        return false;
      }

      // ✅ Mettre à jour l'abonnement localement
      await this.updateSubscriptionAfterPayment(data);

      return true;

    } catch (error) {
      if (error instanceof Error) {
      }
     
      return false;
    }
  }

  /**
   * Mettre à jour l'abonnement après un paiement réussi
   */
  private async updateSubscriptionAfterPayment(paymentData: any): Promise<void> {

    try {
      const { plan, expiresAt, email } = paymentData;

      // Déterminer le tier
      const tier = SubscriptionTier.PREMIUM;

      // Déterminer le billing period
      const billingPeriod: 'monthly' | 'yearly' = plan === 'yearly' ? 'yearly' : 'monthly';

      // Déterminer le planId
      const planId = plan === 'yearly' ? 'premium_yearly' : 'premium_monthly';

      // Calculer la date d'expiration
      const expirationDate = expiresAt ? new Date(expiresAt).getTime() : null;

      // ✅ Mettre à jour le service d'abonnement
      subscriptionService.updateSubscription(
        tier,
        expirationDate,
        `stripe_${paymentData.sessionId}`,
        billingPeriod,
        planId,
        true // paiement confirmé par le backend (/api/stripe/verify-payment)
      );

      // ✅ Mettre à jour le cache avec l'email
      const cacheData = {
        email: email,
        tier: 'premium',
        status: 'active',
        verified: true,
        verifiedBy: 'stripe_payment',
        verifiedAt: Date.now(),
        cachedAt: Date.now(),
        plan_id: planId,
        billing_period: billingPeriod,
        expires_at: expirationDate
      };

      localStorage.setItem('user_subscription', JSON.stringify(cacheData));

      // ✅ Émettre un événement
      window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
        detail: {
          tier: SubscriptionTier.PREMIUM,
          planId: planId,
          billingPeriod: billingPeriod,
          isPremium: true
        }
      }));

    } catch (error) {
throw error;
    }
  }

  /**
   * Créer et rediriger vers le checkout en une seule étape
   */
  async initiatePayment(params: CreateCheckoutSessionParams): Promise<void> {
  
    try {
      // ✅ S'assurer que Stripe est initialisé
      await this.initialize();

      // ✅ Créer la session
      const { sessionId, url } = await this.createCheckoutSession(params);

      // ✅ Rediriger vers Stripe
      await this.redirectToCheckout(sessionId, url);

    } catch (error) {
throw error;
    }
  }

  /**
   * Obtenir l'URL de retour après paiement
   */
  getSuccessUrl(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
  }

  /**
   * Obtenir l'URL d'annulation
   */
  getCancelUrl(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}?payment=cancelled`;
  }


 
  isReady(): boolean {
    return this.isInitialized && this.stripe !== null;
  }

  /**
   * Réinitialiser le service (pour les tests)
   */
  reset(): void {

  }
}

// Singleton
const paymentService = new PaymentService();
export default paymentService;

// Exposer pour debugging uniquement en développement
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).paymentService = paymentService;
}